import React, { useEffect, useState } from "react";
import { Youtube, FileText, Link as LinkIcon, BookMarked } from "lucide-react";
import { api } from "../lib/api";

/** Student dashboard strip — teacher resources from every group the student
 * belongs to. Collapses when there are none. */
export default function MyResources({ lang = "en" }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancel = false;
    api.get("/me/resources")
      .then((r) => { if (!cancel) setItems(r.data); })
      .catch(() => { if (!cancel) setItems([]); });
    return () => { cancel = true; };
  }, []);

  if (!items || items.length === 0) return null;
  const L = lang === "ar";
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  return (
    <section className="mb-8 animate-fade-up" data-testid="my-resources">
      <div className="rounded-2xl border border-border bg-white p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-sage/10 text-sage flex items-center justify-center">
            <BookMarked className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold">{L ? "من معلّمك" : "From your teacher"}</div>
            <h2 className="font-display text-lg font-semibold tracking-tight">{L ? "المصادر" : "Resources"}</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="my-resources-list">
          {items.slice(0, 6).map((r) => (
            <ResourceTile key={r.resource_id} r={r} apiBase={apiBase} />
          ))}
        </div>
        {items.length > 6 && (
          <div className="mt-2 text-xs text-muted-foreground">+{items.length - 6} {L ? "مصادر أخرى" : "more"}</div>
        )}
      </div>
    </section>
  );
}

function ResourceTile({ r, apiBase }) {
  const icon =
    r.type === "youtube" ? <Youtube className="w-4 h-4 text-[#d8382b]" /> :
    r.type === "file" ? <FileText className="w-4 h-4 text-sage" /> :
    <LinkIcon className="w-4 h-4 text-terracotta" />;

  const href = r.type === "youtube" ? r.youtube_url :
               r.type === "file" ? `${apiBase}/api/files/${r.file_id}` :
               r.external_url;
  const thumb = r.type === "youtube" && r.youtube_id
    ? `https://img.youtube.com/vi/${r.youtube_id}/hqdefault.jpg` : null;

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={`resource-tile-${r.resource_id}`}
      className="block bg-bone border border-border rounded-xl p-3 hover:border-sage/50 hover:shadow-sm transition-all"
    >
      {thumb && (
        <div className="aspect-video rounded-md overflow-hidden mb-2 bg-black">
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-md bg-white border border-border flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium leading-snug line-clamp-2">{r.title}</div>
          {r.group_name && (
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{r.group_name}</div>
          )}
        </div>
      </div>
    </a>
  );
}
